import React, { useState, useEffect, useRef } from 'react';
import { acquistiApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Upload, Loader2, FileUp, X, CheckCircle2, AlertTriangle, Info } from 'lucide-react';
import AcquistiHeader from '../../components/acquisti/AcquistiHeader';
import LinkGuida from '../../components/promemoria/LinkGuida';
import { fmtSoldi, fmtDataIt } from '../../lib/acquisti';

const AcquistiCaricamentoPage = () => {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [importando, setImportando] = useState(false);
  const [risultato, setRisultato] = useState(null);
  const [stato, setStato] = useState(null);

  const caricaStato = () => acquistiApi.getStato().then(setStato).catch(() => {});
  useEffect(() => { caricaStato(); }, []);

  const importa = async () => {
    if (!files.length) return;
    setImportando(true);
    setRisultato(null);
    try {
      const res = await acquistiApi.importa(files);
      setRisultato(res);
      setFiles([]);
      if (inputRef.current) inputRef.current.value = '';
      toast.success(`${res.documenti_nuovi} fatture importate, ${res.documenti_duplicati} già presenti`);
      caricaStato();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import non riuscito');
    } finally {
      setImportando(false);
    }
  };

  return (
    <div className="min-h-screen pb-24">
      <AcquistiHeader titolo="Carica fatture" sottotitolo="XML, XML firmati (.p7m) e ZIP del Digital Hub" />

      <main className="px-4 py-4 max-w-lg mx-auto space-y-4">
        <LinkGuida testo="Come si richiedono e si scaricano le fatture XML?" />
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Info className="w-4 h-4 text-primary" /> Come funziona
            </CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground space-y-1.5">
            <p>Puoi caricare più file insieme: <strong>.xml</strong>, <strong>.xml.p7m</strong> firmati e <strong>.zip</strong> con dentro molte fatture.</p>
            <p>I metadati SdI (file <em>_MT_</em>) e le notifiche vengono riconosciuti e messi da parte: non sono fatture.</p>
            <p>Una fattura già importata viene <strong>saltata</strong> (stessa P.IVA, tipo, numero e data): nessun doppio conteggio.</p>
            <p>Le note di credito riducono la spesa e <strong>non</strong> modificano i prezzi di acquisto.</p>
            <p>Lo storico prezzi non viene mai sovrascritto: ogni fattura aggiunge una rilevazione.</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 space-y-3">
            <input
              ref={inputRef}
              type="file"
              multiple
              accept=".xml,.p7m,.zip"
              className="hidden"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              data-testid="acquisti-file-input"
            />
            <Button variant="outline" className="w-full h-12" onClick={() => inputRef.current?.click()} data-testid="scegli-file-btn">
              <FileUp className="w-4 h-4 mr-2" /> Scegli i file da importare
            </Button>

            {files.length > 0 && (
              <div className="space-y-1" data-testid="file-selezionati">
                {files.map((f, i) => (
                  <div key={`${f.name}-${i}`} className="flex items-center justify-between gap-2 text-xs p-2 rounded bg-secondary">
                    <span className="truncate">{f.name}</span>
                    <span className="text-muted-foreground shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
                  </div>
                ))}
                <div className="flex gap-2 pt-1">
                  <Button variant="ghost" size="sm" onClick={() => { setFiles([]); if (inputRef.current) inputRef.current.value = ''; }}>
                    <X className="w-4 h-4 mr-1" /> Svuota
                  </Button>
                  <Button className="flex-1" onClick={importa} disabled={importando} data-testid="importa-btn">
                    {importando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    Importa {files.length} file
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {risultato && (
          <Card data-testid="import-risultato">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Import completato
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><p className="text-[11px] uppercase text-muted-foreground">Fatture nuove</p><p className="font-bold text-lg tabular-nums" data-testid="res-nuovi">{risultato.documenti_nuovi}</p></div>
                <div><p className="text-[11px] uppercase text-muted-foreground">Già presenti</p><p className="font-bold text-lg tabular-nums" data-testid="res-duplicati">{risultato.documenti_duplicati}</p></div>
                <div><p className="text-[11px] uppercase text-muted-foreground">Righe importate</p><p className="font-semibold tabular-nums">{risultato.righe_importate}</p></div>
                <div><p className="text-[11px] uppercase text-muted-foreground">Totale importato</p><p className="font-semibold tabular-nums">{fmtSoldi(risultato.totale_importato)}</p></div>
                <div><p className="text-[11px] uppercase text-muted-foreground">Prodotti nuovi</p><p className="font-semibold tabular-nums">{risultato.prodotti_nuovi}</p></div>
                <div><p className="text-[11px] uppercase text-muted-foreground">Fornitori coinvolti</p><p className="font-semibold tabular-nums">{risultato.fornitori_coinvolti}</p></div>
              </div>

              <div className="text-xs text-muted-foreground space-y-1">
                <p>File analizzati: {risultato.file_analizzati} · metadati SdI riconosciuti: {risultato.metadati_sdi} · file non-fattura: {risultato.non_fatture}</p>
                {(risultato.righe_senza_um > 0 || risultato.righe_senza_quantita > 0) && (
                  <p className="text-amber-700">
                    {risultato.righe_senza_um} righe senza unità di misura e {risultato.righe_senza_quantita} senza
                    quantità: restano indicate come non disponibili e non vengono confrontate.
                  </p>
                )}
              </div>

              {risultato.errori?.length > 0 && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-900" data-testid="import-errori">
                  <p className="font-semibold flex items-center gap-1 mb-1"><AlertTriangle className="w-3.5 h-3.5" /> File con problemi</p>
                  <ul className="list-disc pl-4">
                    {risultato.errori.slice(0, 8).map((e, i) => <li key={i}>{e.file}: {e.motivo}</li>)}
                  </ul>
                </div>
              )}

              {risultato.scartati?.length > 0 && (
                <div className="p-3 rounded-lg bg-secondary text-xs" data-testid="import-scartati">
                  <p className="font-semibold mb-1">File messi da parte (non sono fatture)</p>
                  <ul className="list-disc pl-4 text-muted-foreground">
                    {risultato.scartati.slice(0, 8).map((s, i) => <li key={i}>{s.file}: {s.tipo}</li>)}
                  </ul>
                </div>
              )}

              {risultato.documenti?.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Fatture importate</p>
                  <div className="border rounded-lg divide-y max-h-72 overflow-y-auto">
                    {risultato.documenti.map((d) => (
                      <div key={d.documento_id} className="p-2 text-xs flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-medium truncate">{d.fornitore}</p>
                          <p className="text-muted-foreground">n. {d.numero} · {fmtDataIt(d.data)} · {d.n_righe} righe</p>
                          {d.segno === -1 && <Badge variant="destructive" className="text-[10px] mt-1">{d.tipo_label}</Badge>}
                        </div>
                        <span className="font-semibold tabular-nums shrink-0">{fmtSoldi((d.totale || 0) * d.segno)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {risultato.duplicati?.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Saltate perché già presenti</p>
                  <div className="border rounded-lg divide-y max-h-48 overflow-y-auto" data-testid="import-duplicati">
                    {risultato.duplicati.map((d, i) => (
                      <div key={i} className="p-2 text-xs">
                        <p className="font-medium truncate">{d.fornitore} · n. {d.numero}</p>
                        <p className="text-muted-foreground">{fmtDataIt(d.data)} · {d.motivo}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {stato && (
          <Card data-testid="acquisti-stato">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Archivio acquisti</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-[11px] uppercase text-muted-foreground">Fatture</p><p className="font-semibold tabular-nums">{stato.n_documenti}</p></div>
              <div><p className="text-[11px] uppercase text-muted-foreground">Righe</p><p className="font-semibold tabular-nums">{stato.n_righe}</p></div>
              <div><p className="text-[11px] uppercase text-muted-foreground">Fornitori</p><p className="font-semibold tabular-nums">{stato.n_fornitori}</p></div>
              <div><p className="text-[11px] uppercase text-muted-foreground">Prodotti</p><p className="font-semibold tabular-nums">{stato.n_prodotti}</p></div>
              <div><p className="text-[11px] uppercase text-muted-foreground">Periodo</p><p className="font-semibold">{stato.vuoto ? '—' : `${fmtDataIt(stato.data_min)} → ${fmtDataIt(stato.data_max)}`}</p></div>
              <div><p className="text-[11px] uppercase text-muted-foreground">Totale documenti</p><p className="font-semibold tabular-nums">{fmtSoldi(stato.totale)}</p></div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default AcquistiCaricamentoPage;
